import type { StorageApi, StorageFileListResponse, StorageFile } from '@/lib/api';

/** Max pages to fetch before stopping (100 × 1000 = 100k objects per prefix) */
const MAX_PAGES = 100;

/**
 * Fetches all files from a storage arche by paginating through S3 ListObjectsV2 results.
 * Returns a merged response with all files/folders and the CDN base URL from the first page.
 * Caps at MAX_PAGES to prevent unbounded memory growth in the browser.
 * On mid-pagination errors, returns partial results rather than discarding everything.
 */
export async function fetchAllStorageFiles(
  api: StorageApi,
  prefix: string,
  delimiter: string,
): Promise<StorageFileListResponse> {
  const allFiles: StorageFile[] = [];
  const allFolders: string[] = [];
  let cdnBaseUrl: string | null = null;
  let continuationToken: string | undefined;
  let pages = 0;

  try {
    do {
      const page = await api.listFiles(prefix, delimiter, 1000, continuationToken);
      allFiles.push(...page.files);
      allFolders.push(...page.folders);
      if (cdnBaseUrl === null) cdnBaseUrl = page.cdnBaseUrl;
      continuationToken = page.continuationToken ?? undefined;
      pages++;
    } while (continuationToken && pages < MAX_PAGES);
  } catch (err) {
    // If we already have partial results, return them rather than losing everything
    if (allFiles.length > 0 || allFolders.length > 0) {
      return {
        files: allFiles,
        folders: allFolders,
        cdnBaseUrl,
        continuationToken: null,
        isTruncated: true,
      };
    }
    throw err;
  }

  return {
    files: allFiles,
    folders: allFolders,
    cdnBaseUrl,
    continuationToken: null,
    isTruncated: false,
  };
}

export function downloadTextFile(filename: string, lines: string[]) {
  const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function formatSize(bytes?: number): string {
  if (bytes == null) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDate(iso?: string): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
